import { useQuery } from '@tanstack/react-query';
import { Entity, mapEntity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatPriceDisplay } from '~/components/price-display-view/helpers/price-display-helpers';
import { mapEntityToPriceEntity, useLivePrice } from '~/hooks/use-live-price';
import { useSnakeMeta } from '~/hooks/use-snake/useSnake';
import { getPreliminaryPrice } from '~/services/price/price-service';
import { getPriceData } from '~/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';

import * as S from './company-card/CompanyCard.styles';

export interface PriceChangeProps {
  companyData: Entity;
}

export const PriceChange = ({ companyData }: PriceChangeProps) => {
  const { t } = useTranslation(['earnings']);

  const mappedEntity = mapEntity(companyData);
  const { data: snakeMeta } = useSnakeMeta(companyData?.default_snake);
  const { data: preliminaryPrice } = useQuery({
    queryKey: [companyData.subscribable_ticker],
    queryFn: () =>
      getPreliminaryPrice(companyData.subscribable_ticker as string),
    enabled: !!companyData.subscribable_ticker,
  });

  const priceEntities = mapEntityToPriceEntity(companyData, snakeMeta);
  const livePriceData = useLivePrice(priceEntities);

  const livePriceInfo = getPriceData({
    hoverData: undefined,
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });
  const price = formatPriceDisplay({
    exchange: companyData.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice?.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  if (!livePriceInfo || !price.primary.priceChange) {
    return null;
  }

  return (
    <S.PriceChangeWrapper>
      <S.PriceChangeContainer $status={price.primary.priceChange.status}>
        {price.primary.priceChange.proportionChange}
      </S.PriceChangeContainer>
      <S.DateLabel>
        {t(
          price.primary.isLive
            ? `earningsInsight.drawer.earningsRecap.liveData`
            : `earningsInsight.drawer.earningsRecap.asOfLastClose`
        )}
      </S.DateLabel>
    </S.PriceChangeWrapper>
  );
};
